import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Form, Dropdown, Table, InputGroup, Nav, OverlayTrigger, Popover } from 'react-bootstrap';
import { getEnvelopes, deleteEnvelope, revokeEnvelope, copyEnvelope } from '../../services/envelopeService';
import { formatDateUTC, parseApiDateTimeAsUtc } from '../../utils/dateFormat';
import { getActiveOrganizationId } from '../../utils/organizations';

const Drafts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const activeOrganizationId = getActiveOrganizationId(user);
  
  // Determine active tab from route
  const getTabFromRoute = (pathname) => {
    if (pathname.includes('/sent')) return 'sent';
    if (pathname.includes('/completed')) return 'completed';
    if (pathname.includes('/revoked')) return 'revoked';
    return 'draft'; // default to draft
  };
  
  const [activeTab, setActiveTab] = useState(() => getTabFromRoute(location.pathname)); // 'draft', 'sent', 'completed', 'revoked'
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('Last 6 months');
  const [showDateFilter, setShowDateFilter] = useState(false);
  // const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [sortColumn, setSortColumn] = useState('last_change');
  const [sortDirection, setSortDirection] = useState('desc');
  const [copyingEnvelopeId, setCopyingEnvelopeId] = useState(null);

  // Date filter options
  const dateFilterOptions = [
    'All time',
    'Last 12 months',
    'Last 6 months',
    'Last 30 days',
    'Last week',
    'Last 24 hours',
    'Custom'
  ];

  // Update active tab when route changes
  useEffect(() => {
    const tabFromRoute = getTabFromRoute(location.pathname);
    if (tabFromRoute !== activeTab) {
      setActiveTab(tabFromRoute);
    }
  }, [location.pathname, activeTab]);

  // Load envelopes when tab changes
  useEffect(() => {
    loadEnvelopes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeOrganizationId]);

  const loadEnvelopes = async () => {
    try {
      setLoading(true);
      const response = await getEnvelopes(activeTab);
      // API returns: { success: true, data: [...], filters: {...}, meta: {...} }
      // getEnvelopes already returns response.data, so we need response.data.data
      const envelopesData = response?.data || [];
      setDrafts(envelopesData);
    } catch (error) {
      console.error('Error loading envelopes:', error);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return {
      date: formatDateUTC(dateString),
      time: ''
    };
  };

  const getRecipientLabels = (envelope) => {
    const recipients =
      envelope?.recipients ||
      envelope?.envelope?.recipients ||
      envelope?.envelope?.recipient ||
      [];
    if (Array.isArray(recipients) && recipients.length > 0) {
      return recipients
        .map((r) => r?.name || r?.resolved_name || r?.email || r?.resolved_email || r?.signee_placeholder || r?.placeholder || '')
        .filter(Boolean);
    }

    // Fallback for Sent/Completed: API often returns signees under `progress.*_signees`.
    const p = envelope?.progress || {};
    const progressSignees = [
      ...(Array.isArray(p.active_signees) ? p.active_signees : []),
      ...(Array.isArray(p.completed_signees) ? p.completed_signees : []),
      ...(Array.isArray(p.declined_signees) ? p.declined_signees : []),
    ];

    return progressSignees
      .map((r) => r?.name || r?.resolved_name || r?.email || r?.resolved_email || r?.signee_placeholder || r?.placeholder || '')
      .filter(Boolean);
  };

  // Get document title
  const getDocumentTitle = (envelope) => {
    if (envelope.document?.title) {
      return envelope.document.title;
    }
    if (envelope.document_title) {
      return envelope.document_title;
    }
    if (envelope.subject) {
      return envelope.subject;
    }
    return '[Untitled]';
  };

  // Get status display
  const getStatusDisplay = (envelope) => {
    const status = envelope.status || 'draft';
    switch (status) {
      case 'sent':
        return { icon: 'ri-send-plane-line', text: 'Sent', color: 'info' };
      case 'completed':
        return { icon: 'ri-checkbox-circle-line', text: 'Completed', color: 'success' };
      case 'revoked':
        return { icon: 'ri-close-circle-line', text: 'Revoked', color: 'warning' };
      default:
        return { icon: 'ri-file-edit-line', text: 'Draft', color: 'primary' };
    }
  };

  const getCreatedByName = (envelope) => {
    const directName =
      envelope?.created_by_name ||
      envelope?.creator_name ||
      envelope?.owner_name ||
      envelope?.user_name ||
      envelope?.created_by_user_name ||
      '';
    if (directName) return directName;

    const creator =
      envelope?.created_by_user ||
      envelope?.created_by ||
      envelope?.creator ||
      envelope?.owner ||
      envelope?.user ||
      null;

    if (typeof creator === 'string') return creator;
    if (creator && typeof creator === 'object') {
      return (
        creator.name ||
        creator.full_name ||
        creator.display_name ||
        creator.email ||
        creator.username ||
        ''
      );
    }

    return '-';
  };

  // Get page title based on active tab
  const getPageTitle = () => {
    switch (activeTab) {
      case 'sent':
        return 'Sent Envelopes';
      case 'completed':
        return 'Completed Envelopes';
      case 'revoked':
        return 'Revoked Envelopes';
      default:
        return 'Drafts';
    }
  };

  // Handle continue - navigate to envelope progress page
  const handleContinue = (envelopeId) => {
    if (!envelopeId) {
      console.error('Cannot continue: envelope ID is missing');
      alert('Error: Draft ID is missing. Please refresh the page and try again.');
      return;
    }
    // Navigate to envelope progress page
    navigate(`/envelopes/${envelopeId}/progress`);
  };

  // Handle delete
  const handleDelete = async (envelopeId) => {
    // Safety guard: deletion is allowed only on Drafts tab
    if (activeTab !== 'draft' && activeTab !== 'revoked') {
      console.warn('Delete action blocked: only allowed in Drafts/Revoked tab');
      return;
    }

    if (!envelopeId) {
      console.error('Cannot delete: envelope ID is missing');
      alert('Error: Draft ID is missing. Please refresh the page and try again.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this draft?')) {
      try {
        await deleteEnvelope(envelopeId);
        await loadEnvelopes(); // Reload list
      } catch (error) {
        console.error('Error deleting draft:', error);
        alert('Error deleting draft. Please try again.');
      }
    }
  };

  // Handle save as template
  const handleSaveAsTemplate = async (envelopeId) => {
    if (!envelopeId) {
      console.error('Cannot save as template: envelope ID is missing');
      alert('Error: Draft ID is missing. Please refresh the page and try again.');
      return;
    }
    try {
      // TODO: Implement save as template API call
      alert('Save as template functionality coming soon');
    } catch (error) {
      console.error('Error saving as template:', error);
    }
  };

  // Handle transfer ownership
  const handleTransferOwnership = async (envelopeId) => {
    if (!envelopeId) {
      console.error('Cannot transfer ownership: envelope ID is missing');
      alert('Error: Draft ID is missing. Please refresh the page and try again.');
      return;
    }
    try {
      // TODO: Implement transfer ownership API call
      alert('Transfer ownership functionality coming soon');
    } catch (error) {
      console.error('Error transferring ownership:', error);
    }
  };

  // Completed: view envelope details in frontend page
  const handleViewEnvelope = (envelopeId) => {
    if (!envelopeId) return;
    navigate(`/envelopes/${envelopeId}/progress`);
  };

  // Sent: view envelope progress in frontend page
  const handleViewProgress = (envelopeId) => {
    if (!envelopeId) return;
    navigate(`/envelopes/${envelopeId}/progress`);
  };

  // Sent: revoke envelope
  const handleRevoke = async (envelopeId) => {
    if (!envelopeId) return;
    if (activeTab !== 'sent') return;
    if (!window.confirm('Are you sure you want to revoke this envelope?')) return;
    try {
      await revokeEnvelope(envelopeId);
      await loadEnvelopes();
    } catch (err) {
      console.error('Error revoking envelope:', err);
      alert(err?.response?.data?.message || 'Failed to revoke envelope.');
    }
  };

  const getCopiedEnvelopeId = (response) => {
    const data = response?.data || response || {};
    return (
      data.envelope_id ||
      data.id ||
      data.envelope?.id ||
      data.envelope?.envelope_id ||
      data.data?.envelope_id ||
      data.data?.id ||
      data.data?.envelope?.id ||
      data.data?.envelope?.envelope_id ||
      null
    );
  };

  const handleCopyEnvelope = async (envelopeId) => {
    if (!envelopeId || copyingEnvelopeId) return;
    try {
      setCopyingEnvelopeId(envelopeId);
      const response = await copyEnvelope(envelopeId);
      const copiedEnvelopeId = getCopiedEnvelopeId(response);
      if (!copiedEnvelopeId) {
        throw new Error('Envelope copied, but no new envelope ID was returned.');
      }
      navigate(`/create-envelope/${copiedEnvelopeId}`);
    } catch (err) {
      console.error('Error copying envelope:', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to copy envelope.');
    } finally {
      setCopyingEnvelopeId(null);
    }
  };

  // Filter drafts based on search and date
  const filteredDrafts = drafts.filter(draft => {
    // Search filter
    if (searchQuery) {
      const title = getDocumentTitle(draft).toLowerCase();
      const recipients = getRecipientLabels(draft).join(', ').toLowerCase();
      const createdBy = getCreatedByName(draft).toLowerCase();
      const searchLower = searchQuery.toLowerCase();
      if (!title.includes(searchLower) && !recipients.includes(searchLower) && !createdBy.includes(searchLower)) {
        return false;
      }
    }

    // Date filter
    if (dateFilter && dateFilter !== 'All time') {
      // Use sent_at for sent/completed envelopes, updated_at/created_at for drafts
      const draftDate = parseApiDateTimeAsUtc(draft.sent_at || draft.updated_at || draft.created_at);
      const now = new Date();
      let daysBack = 0;

      switch (dateFilter) {
        case 'Last 24 hours':
          daysBack = 1;
          break;
        case 'Last week':
          daysBack = 7;
          break;
        case 'Last 30 days':
          daysBack = 30;
          break;
        case 'Last 6 months':
          daysBack = 180;
          break;
        case 'Last 12 months':
          daysBack = 365;
          break;
        default:
          return true; // Custom or unknown filter
      }

      const cutoffDate = new Date(now);
      cutoffDate.setDate(cutoffDate.getDate() - daysBack);
      
      if (!draftDate || draftDate < cutoffDate) {
        return false;
      }
    }

    return true;
  });

  // Sort drafts
  const sortedDrafts = [...filteredDrafts].sort((a, b) => {
    let aValue, bValue;
    
    if (sortColumn === 'last_change') {
      aValue = parseApiDateTimeAsUtc(a.updated_at || a.created_at || 0) || new Date(0);
      bValue = parseApiDateTimeAsUtc(b.updated_at || b.created_at || 0) || new Date(0);
    } else if (sortColumn === 'name') {
      aValue = getDocumentTitle(a).toLowerCase();
      bValue = getDocumentTitle(b).toLowerCase();
    } else if (sortColumn === 'created_by') {
      aValue = getCreatedByName(a).toLowerCase();
      bValue = getCreatedByName(b).toLowerCase();
    } else {
      return 0;
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Toggle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Toggle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDrafts(
        sortedDrafts
          .map(d => d.id || d.envelope_id || d.template_id)
          .filter(id => id !== undefined && id !== null)
      );
    } else {
      setSelectedDrafts([]);
    }
  };

  // Toggle individual selection
  const handleSelectDraft = (draftId) => {
    setSelectedDrafts(prev => 
      prev.includes(draftId)
        ? prev.filter(id => id !== draftId)
        : [...prev, draftId]
    );
  };

  // Clear filters
  const handleClear = () => {
    setSearchQuery('');
    setDateFilter('Last 6 months');
  };

  return (
    <div className="drafts-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="drafts-title">{getPageTitle()}</h1>
      </div>

      {/* Tabs */}
      <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={(k, e) => {
        if (e) {
          e.preventDefault();
        }
        // Only navigate - activeTab will be updated by useEffect when route changes
        if (k === 'sent') {
          navigate('/envelopes/sent');
        } else if (k === 'completed') {
          navigate('/envelopes/completed');
        } else if (k === 'revoked') {
          navigate('/envelopes/revoked');
        } else {
          navigate('/envelopes/drafts');
        }
      }}>
        <Nav.Item>
          <Nav.Link eventKey="draft" href="#" onClick={(e) => e.preventDefault()}>
            <i className="ri-file-edit-line me-2"></i>
            Drafts
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="sent" href="#" onClick={(e) => e.preventDefault()}>
            <i className="ri-send-plane-line me-2"></i>
            Sent
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="completed" href="#" onClick={(e) => e.preventDefault()}>
            <i className="ri-checkbox-circle-line me-2"></i>
            Completed
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="revoked" href="#" onClick={(e) => e.preventDefault()}>
            <i className="ri-close-circle-line me-2"></i>
            Revoked
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Search and Filter Bar */}
      <div className="drafts-filters mb-4">
        <div className="d-flex gap-2 align-items-center flex-wrap drafts-filters-row">
          <InputGroup className="search-input-group" style={{ flex: '1', minWidth: 0 }}>
            <InputGroup.Text>
              <i className="ri-search-line"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={`Search ${getPageTitle()}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>

          <Dropdown show={showDateFilter} onToggle={setShowDateFilter}>
            <Dropdown.Toggle variant="outline-secondary" className="filter-btn">
              Date
            </Dropdown.Toggle>
            <Dropdown.Menu className="date-filter-menu">
              <div className="px-3 py-2 border-bottom">
                <strong>Date</strong>
              </div>
              {dateFilterOptions.map((option) => (
                <Dropdown.Item key={option} as="div" className="px-3 py-2">
                  <Form.Check
                    type="radio"
                    name="dateFilter"
                    id={`date-${option}`}
                    label={option}
                    checked={dateFilter === option}
                    onChange={() => setDateFilter(option)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown.Item>
              ))}
              <div className="px-3 py-2 border-top d-flex gap-2 justify-content-end">
                <Button variant="outline-secondary" size="sm" onClick={(e) => {
                  e.stopPropagation();
                  setDateFilter('Last 6 months');
                }}>
                  Reset
                </Button>
                <Button variant="primary" size="sm" onClick={(e) => {
                  e.stopPropagation();
                  setShowDateFilter(false);
                }}>
                  Apply
                </Button>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          {/* <Dropdown show={showAdvancedSearch} onToggle={setShowAdvancedSearch}>
            <Dropdown.Toggle variant="outline-secondary" className="filter-btn">
              Advanced search <i className="ri-arrow-down-s-line ms-1"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item>Advanced search options coming soon</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown> */}

          <Button variant="outline-secondary" onClick={handleClear}>
            Clear
          </Button>
        </div>
      </div>

      {/* Drafts Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : sortedDrafts.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">
            No {activeTab === 'draft' ? 'drafts' : activeTab === 'sent' ? 'sent envelopes' : activeTab === 'completed' ? 'completed envelopes' : 'revoked envelopes'} found
          </p>
        </div>
      ) : (
        <div className="drafts-table-wrapper">
          <Table hover className="drafts-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedDrafts.length === sortedDrafts.length && sortedDrafts.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>
                  <div 
                    className="d-flex align-items-center cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    NAME
                    <i className={`ri-arrow-${sortColumn === 'name' && sortDirection === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                  </div>
                </th>
                <th>STATUS</th>
                <th>
                  <div
                    className="d-flex align-items-center cursor-pointer"
                    onClick={() => handleSort('created_by')}
                  >
                    CREATED BY
                    <i className={`ri-arrow-${sortColumn === 'created_by' && sortDirection === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                  </div>
                </th>
                <th>
                  <div 
                    className="d-flex align-items-center cursor-pointer"
                    onClick={() => handleSort('last_change')}
                  >
                    LAST CHANGE
                    <i className={`ri-arrow-${sortColumn === 'last_change' && sortDirection === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                  </div>
                </th>
                <th style={{ width: '150px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {sortedDrafts.map((draft) => {
                // Try multiple possible ID field names
                const draftId = draft.id || draft.envelope_id || draft.template_id;
                const dateInfo = formatDate(draft.updated_at || draft.created_at);
                const isSelected = selectedDrafts.includes(draftId);
                
                // Skip rendering if no valid ID found
                if (!draftId) {
                  console.warn('Draft missing ID field:', draft);
                  return null;
                }
                
                return (
                  <tr key={draftId}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectDraft(draftId)}
                      />
                    </td>
                    <td>
                      <div>
                        <div className="draft-name">{getDocumentTitle(draft)}</div>
                        <div className="draft-recipients text-muted small">
                          {(() => {
                            const isDraftTab = activeTab === 'draft' || draft.status === 'draft';
                            const recipientsCount =
                              draft?.recipients_count ?? draft?.recipientsCount ?? null;

                            // Draft envelopes come back without full recipient objects.
                            // In that case, just show the count.
                            if (isDraftTab) {
                              if (recipientsCount == null) return 'No recipients';
                              return `${recipientsCount} recipient${String(recipientsCount) === '1' ? '' : 's'}`;
                            }

                            const labels = getRecipientLabels(draft);
                            if (!labels.length) return 'No recipients';

                            const firstTwo = labels.slice(0, 2);
                            const hasMore = labels.length > 2;
                            const popover = (
                              <Popover id={`recipients-pop-${draftId}`}>
                                <Popover.Header>Recipients</Popover.Header>
                                <Popover.Body>
                                  <ul className="mb-0">
                                    {labels.map((l, i) => (
                                      <li key={`${l}-${i}`}>{l}</li>
                                    ))}
                                  </ul>
                                </Popover.Body>
                              </Popover>
                            );

                            return (
                              <>
                                <span>{`To: ${firstTwo.join(', ')}`}</span>
                                {hasMore && (
                                  <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement="top"
                                    overlay={popover}
                                  >
                                    <span
                                      className="ms-2"
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                      View more
                                    </span>
                                  </OverlayTrigger>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <i className={`${getStatusDisplay(draft).icon} text-${getStatusDisplay(draft).color}`}></i>
                        <span>{getStatusDisplay(draft).text}</span>
                      </div>
                    </td>
                    <td>
                      <div className="text-muted small">{getCreatedByName(draft)}</div>
                    </td>
                    <td>
                      <div>
                        <div>{dateInfo.date}</div>
                        {dateInfo.time ? <div className="text-muted small">{dateInfo.time}</div> : null}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {(activeTab === 'draft' || activeTab === 'revoked') && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleContinue(draftId)}
                          >
                            {activeTab === 'revoked' ? 'View Details' : 'Continue'}
                          </Button>
                        )}
                        {activeTab === 'revoked' && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleCopyEnvelope(draftId)}
                            disabled={copyingEnvelopeId === draftId}
                          >
                            {copyingEnvelopeId === draftId ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Copying...
                              </>
                            ) : (
                              <>
                                <i className="ri-file-copy-line me-1"></i>
                                Copy Envelope
                              </>
                            )}
                          </Button>
                        )}
                        {activeTab !== 'revoked' && (
                        <Dropdown>
                          <Dropdown.Toggle variant="link" className="text-reset p-0" id={`draft-menu-${draftId}`}>
                            <i className="ri-more-2-fill"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu align="end">
                            {activeTab === 'draft' && (
                              <>
                                <Dropdown.Item onClick={() => handleSaveAsTemplate(draftId)}>
                                  <i className="ri-file-list-3-line me-2"></i>
                                  Save as Template
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleTransferOwnership(draftId)}>
                                  <i className="ri-user-transfer-line me-2"></i>
                                  Transfer Ownership
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item 
                                  className="text-danger"
                                  onClick={() => handleDelete(draftId)}
                                >
                                  <i className="ri-delete-bin-line me-2"></i>
                                  Delete
                                </Dropdown.Item>
                              </>
                            )}
                            {activeTab === 'sent' && (
                              <>
                                <Dropdown.Item onClick={() => handleViewProgress(draftId)}>
                                  <i className="ri-time-line me-2"></i>
                                  View Progress
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleCopyEnvelope(draftId)}
                                  disabled={copyingEnvelopeId === draftId}
                                >
                                  {copyingEnvelopeId === draftId ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Copying...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-file-copy-line me-2"></i>
                                      Copy Envelope
                                    </>
                                  )}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item className="text-danger" onClick={() => handleRevoke(draftId)}>
                                  <i className="ri-forbid-line me-2"></i>
                                  Revoke Envelope
                                </Dropdown.Item>
                              </>
                            )}
                            {activeTab === 'completed' && (
                              <>
                                <Dropdown.Item
                                  onClick={() => handleViewEnvelope(draftId)}
                                  disabled={draft.can_view_completed_details === false}
                                >
                                  <i className="ri-eye-line me-2"></i>
                                  View Envelope
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleCopyEnvelope(draftId)}
                                  disabled={copyingEnvelopeId === draftId}
                                >
                                  {copyingEnvelopeId === draftId ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Copying...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-file-copy-line me-2"></i>
                                      Copy Envelope
                                    </>
                                  )}
                                </Dropdown.Item>
                              </>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default Drafts;

